import React, { useState, useEffect } from 'react';
import { RefundConfirmationModal } from '../common';

const RefundModal = ({ isOpen, onClose, onConfirm, dealValue = 0, dealCurrency = 'USD' }) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Clear refund amount when modal opens/closes or deal changes
  useEffect(() => {
    setRefundAmount('');
  }, [isOpen, dealValue]);
  
  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen && !showConfirmation) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, showConfirmation]);
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Show confirmation dialog instead of immediately confirming
    setShowConfirmation(true);
  };
  
  const executeRefund = () => {
    // Parse the string value to float, or default to 0 if invalid
    const amount = refundAmount === '' ? 0 : parseFloat(refundAmount);
    onConfirm(amount || 0);
    setShowConfirmation(false);
  };
  
  if (!isOpen) return null;
  
  // Format the currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: dealCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Enter Refund Amount</h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Refund Amount ({dealCurrency})
              </label>
              <input
                type="number"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={`Enter amount (max: ${formatCurrency(dealValue)})`}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                autoFocus
                required
              />
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Original deal amount: {formatCurrency(dealValue)}
              </p>
              {refundAmount !== '' && parseFloat(refundAmount) > dealValue && (
                <p className="text-sm text-red-600 mt-1">
                  Warning: Refund amount exceeds original deal value
                </p>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-yellow-600 text-white rounded-md shadow-sm hover:bg-yellow-700"
              >
                Process Refund
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Confirmation Modal */}
      <RefundConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={executeRefund}
        amount={refundAmount}
        currency={dealCurrency}
        message={`Are you sure you want to process a refund of ${formatCurrency(refundAmount)}? This action cannot be undone.`}
      />
    </>
  );
};

export default RefundModal;